import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ImageIcon, XIcon } from 'lucide-react';
import { Reveal, stagger } from '../motion/Reveal';
import type { BcmPhoto } from '../../data/photos';
type PhotoGalleryProps = {
  photos: BcmPhoto[];
};

/**
 * Accessible gallery grid with a keyboard-operable lightbox.
 * Arrow keys move between photos, Escape closes, focus returns to the trigger.
 */
export function PhotoGallery({
  photos
}: PhotoGalleryProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const closeRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => {
    const previous = openIndex;
    setOpenIndex(null);
    if (previous !== null) triggerRefs.current[previous]?.focus();
  }, [openIndex]);
  const step = useCallback((delta: number) => {
    setOpenIndex(current => {
      if (current === null) return current;
      return (current + delta + photos.length) % photos.length;
    });
  }, [photos.length]);
  useEffect(() => {
    if (openIndex === null) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    }
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openIndex, close, step]);
  const active = openIndex === null ? null : photos[openIndex];
  return <>
      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-id="element-368" data-mp="isxr0c2nk177">
        {photos.map((photo, index) => <Reveal key={photo.id} as="li" delay={stagger(index % 4)} data-id="element-369" data-mp="2yk2ezg9zufo4" data-mpi={index}>
            <button type="button" ref={node => {
          triggerRefs.current[index] = node;
        }} onClick={() => setOpenIndex(index)} className="group block w-full overflow-hidden rounded-[18px] border-2 border-brand-navy bg-brand-blue text-left transition-transform duration-150 hover:-translate-y-0.5" data-id="element-370" data-mp="33jf3e5gijust">
              {photo.src ? <img src={photo.src} alt={photo.alt} loading="lazy" decoding="async" className="aspect-square w-full object-cover" data-id="element-371" data-mp="25qp60mcusc3o" /> : <span className="flex aspect-square w-full flex-col items-center justify-center gap-2 px-3 text-center" data-id="element-372" data-mp="1vu2mc0w9jr38">
                  <ImageIcon aria-hidden="true" className="h-5 w-5 text-brand-navy" data-id="element-373" data-mp="1mrb9bwnqon9h" />
                  <span className="font-meta text-[13px] font-semibold text-brand-navy" data-id="element-374" data-mp="3m45k45ixgt7m">
                    Add Image here
                  </span>
                </span>}
              {photo.src && photo.caption ? <span className="block border-t-2 border-brand-navy bg-white px-3 py-2 font-meta text-[12px] text-ink" data-id="element-375" data-mp="3opdb7g1u0wm9">
                  {photo.caption}
                </span> : null}
              <span className="sr-only" data-id="element-376" data-mp="28qymc1jwhzz">
                {photo.src ? `Open larger view: ${photo.alt}` : 'Open larger view: image placeholder'}
              </span>
            </button>
          </Reveal>)}
      </ul>

      {active ? <div role="dialog" aria-modal="true" aria-label={active.src ? active.caption ?? active.subject : 'Image placeholder'} className="on-dark fixed inset-0 z-[60] flex flex-col bg-brand-navy/95 p-4 sm:p-8" data-id="element-377" data-mp="35ru086vtd3x7">
          <div className="flex items-center justify-between" data-id="element-378" data-mp="3jqjvdgz2otix">
            <p className="font-meta text-[12px] uppercase tracking-[0.16em] text-brand-blue" data-id="element-379" data-mp="uxod042n5ung">
              {openIndex !== null ? `${openIndex + 1} of ${photos.length}` : ''}
            </p>
            <button type="button" ref={closeRef} onClick={close} className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-white text-white" data-id="element-380" data-mp="xi4auaff8cez">
              <XIcon aria-hidden="true" className="h-6 w-6" data-id="element-381" data-mp="17hblgpurtsye" />
              <span className="sr-only" data-id="element-382" data-mp="12h7838p5tece">Close photo viewer</span>
            </button>
          </div>

          <div className="flex flex-1 items-center justify-center gap-3 py-4 sm:gap-6" data-id="element-383" data-mp="1ggoyttm0n1oe">
            <button type="button" onClick={() => step(-1)} className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-white text-white" data-id="element-384" data-mp="1bhvjrwr2z2m4">
              <ChevronLeftIcon aria-hidden="true" className="h-6 w-6" data-id="element-385" data-mp="1l7f4z929n8k5" />
              <span className="sr-only" data-id="element-386" data-mp="1z7g5uoij6fzm">Previous photo</span>
            </button>

            <figure className="max-h-full w-full max-w-3xl overflow-hidden rounded-[24px] border-2 border-white bg-brand-blue" data-id="element-387" data-mp="1z53oodklk9nm">
              {active.src ? <img src={active.src} alt={active.alt} className="max-h-[65vh] w-full object-contain" data-id="element-388" data-mp="2dvgi3fo7hb99" /> : <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-3 px-6 text-center" data-id="element-389" data-mp="24sfdjjezns7p">
                  <ImageIcon aria-hidden="true" className="h-7 w-7 text-brand-navy" data-id="element-390" data-mp="29r0fbn9w2jy9" />
                  <p className="font-meta text-[15px] font-semibold text-brand-navy" data-id="element-391" data-mp="1v2vsc73szm9o">
                    Add Image here
                  </p>
                </div>}
              {active.src ? <figcaption className="border-t-2 border-white bg-brand-navy px-4 py-3 font-meta text-[13px] text-white" data-id="element-392" data-mp="1m4k58f25b6lz">
                  {active.caption ?? active.subject}
                </figcaption> : null}
            </figure>

            <button type="button" onClick={() => step(1)} className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-white text-white" data-id="element-393" data-mp="1h3mma09ba8jf">
              <ChevronRightIcon aria-hidden="true" className="h-6 w-6" data-id="element-394" data-mp="3e1aejx65mzw2" />
              <span className="sr-only" data-id="element-395" data-mp="3a08fbgc8mnu3">Next photo</span>
            </button>
          </div>
        </div> : null}
    </>;
}